// Zehnplus: consent helpers on top of Shopify's cookie banner (Customer Privacy API).
//
// 1. Links with href="#cookie-einstellungen" reopen the banner's preferences, so a visitor can
//    change or withdraw consent at any time.
// 2. window.zpConsent.when('analytics' | 'marketing' | 'preferences', callback) runs the callback
//    once the visitor has allowed that purpose, now or later. Any tracking script the theme
//    loads itself must go through it. Shopify's own pixels and app pixels handle consent themselves.

const BANNER_SCRIPT = 'https://cdn.shopify.com/shopifycloud/privacy-banner/storefront-banner.js';

const checks = {
  analytics: (api) => api.analyticsProcessingAllowed(),
  marketing: (api) => api.marketingAllowed(),
  preferences: (api) => api.preferencesProcessingAllowed(),
};

/** @type {Promise<any> | null} */
let apiPromise = null;

function loadApi() {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    const shopify = /** @type {any} */ (window).Shopify;
    if (shopify?.customerPrivacy) return resolve(shopify.customerPrivacy);
    if (!shopify?.loadFeatures) return resolve(null);
    shopify.loadFeatures([
      {
        name: 'consent-tracking-api',
        version: '0.1',
        onLoad: (/** @type {unknown} */ error) => resolve(error ? null : shopify.customerPrivacy),
      },
    ]);
  });
  return apiPromise;
}

/**
 * @param {'analytics' | 'marketing' | 'preferences'} purpose
 * @param {() => void} callback
 */
async function when(purpose, callback) {
  const api = await loadApi();
  // Without the API there is no proof of consent, so nothing is loaded.
  if (!api || !checks[purpose]) return;

  let done = false;
  const run = () => {
    if (done || !checks[purpose](api)) return;
    done = true;
    callback();
  };
  run();
  document.addEventListener('visitorConsentCollected', run);
}

function loadBannerScript() {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = BANNER_SCRIPT;
    script.onload = resolve;
    script.onerror = reject;
    document.head.append(script);
  });
}

async function showPreferences() {
  const page = /** @type {any} */ (window);
  if (!page.privacyBanner) {
    try {
      await loadBannerScript();
    } catch {
      return;
    }
  }
  page.privacyBanner?.showPreferences();
}

document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target);
  const link = target?.closest('a[href$="#cookie-einstellungen"]');
  if (!link) return;
  event.preventDefault();
  showPreferences();
});

/** @type {any} */ (window).zpConsent = { when, showPreferences };
